import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getCollection } from '../services/firestoreService';
import { COLORS } from '../theme/constants';

const { primary: PRIMARY, muted: MUTED } = COLORS;

const FUENTES = [
  { key: 'clinicalRecords', label: 'Ficha SOAP' },
  { key: 'egresosHospitalarios', label: 'Egreso hospitalario' },
  { key: 'surgeryProtocols', label: 'Protocolo quirúrgico' },
];

const FUENTE_COLOR = {
  clinicalRecords: '#DBEAFE',
  egresosHospitalarios: '#D1FAE5',
  surgeryProtocols: '#FEF3C7',
};

const getDate = (value) => {
  if (!value) return null;
  if (value.seconds) return new Date(value.seconds * 1000);
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (value) => {
  const d = getDate(value);
  return d ? d.toLocaleDateString('es-CL') : '–';
};

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const GROUP_CANON_PAIRS = [
  ['Cardiología y Respiratorio', 'Cardiología y Respiratorio'],
  ['Enfermedades Infecciosas', 'Enfermedades Infecciosas'],
  ['Enfermedades Infecciosas (Virales, Bacterianas y Sistémicas)', 'Enfermedades Infecciosas'],
  ['Neurología', 'Neurología'],
  ['Hematología e Inmunología', 'Hematología e Inmunología'],
  ['Dermatología', 'Dermatología'],
  ['Dermatología (Piel y Oídos)', 'Dermatología'],
  ['Nefrología y Urología', 'Nefrología y Urología'],
  ['Sistema Urinario y Renal', 'Nefrología y Urología'],
  ['Odontología', 'Odontología'],
  ['Odontología (Dientes y Boca)', 'Odontología'],
  ['Endocrinología', 'Endocrinología'],
  ['Gastroenterología y Digestivo', 'Gastroenterología y Digestivo'],
  ['Gastrointestinal', 'Gastroenterología y Digestivo'],
  ['Oftalmología', 'Oftalmología'],
  ['Oftalmología (Ojos)', 'Oftalmología'],
  ['Oncología', 'Oncología'],
  ['Trauma y Urgencias', 'Trauma y Urgencias'],
  ['Trauma, Urgencias y Cuidados Críticos', 'Trauma y Urgencias'],
  ['Ortopedia y Musculoesquelético', 'Ortopedia y Musculoesquelético'],
  ['Musculoesquelético (Trauma y Ortopedia)', 'Ortopedia y Musculoesquelético'],
  ['Reproducción y Ginecología', 'Reproducción y Ginecología'],
  ['Reproducción y Genitourinario', 'Reproducción y Ginecología'],
  ['Toxicología (Venenos e Intoxicaciones)', 'Toxicología'],
  ['Comportamiento (Etología Clínica)', 'Etología Clínica'],
  ['Nutrición y Metabólico', 'Nutrición y Metabólico'],
  ['Cirugía / Complicaciones postoperatorias', 'Cirugía y Complicaciones Postoperatorias'],
  ['Procedimientos Generales / Medicina Preventiva', 'Procedimientos Generales / Medicina Preventiva'],
];

const GROUP_CANON = Object.fromEntries(
  GROUP_CANON_PAIRS.map(([raw, canon]) => [normalize(raw), canon])
);

const canonicalGroup = (raw) => {
  if (!raw) return null;
  const n = normalize(raw);
  if (GROUP_CANON[n]) return GROUP_CANON[n];
  return String(raw).trim() || null;
};

const unique = (arr) => Array.from(new Set(arr)).sort();

const extractName = (label = '') => {
  const s = String(label).trim();
  const parts = s.split(' - ');
  return parts.length > 1 ? parts.slice(1).join(' - ').trim() : s;
};

const calcAgeYears = (birthDate) => {
  if (!birthDate) return null;
  const d = getDate(birthDate);
  if (!d) return null;
  const diff = new Date() - d;
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
};

const toNumber = (v) => (v === '' || v == null ? null : Number(v));

function useDebounce(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function ClinicalCaseSearch() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState('all');
  const [catalog, setCatalog] = useState([]);
  const [patients, setPatients] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [filters, setFilters] = useState({
    q: '',
    source: [],
    species: [],
    breed: '',
    sex: '',
    ageMin: '',
    ageMax: '',
    dateFrom: '',
    dateTo: '',
    diagnosis: '',
    group: [],
    vet: '',
    outcome: '',
    complications: 'all',
    deceased: 'all',
  });

  const debouncedQ = useDebounce(filters.q, 300);

  const patientsMap = useMemo(() => {
    const byId = new Map();
    const byFicha = new Map();
    for (const p of patients) {
      if (p.id) byId.set(p.id, p);
      if (p.ficha) byFicha.set(String(p.ficha).trim(), p);
    }
    return { byId, byFicha };
  }, [patients]);

  const catalogByCode = useMemo(() => {
    const byCode = new Map();
    const byName = new Map();
    for (const d of catalog) {
      if (d.code) byCode.set(String(d.code).trim(), d);
      if (d.id) byCode.set(String(d.id), d);
      const n = normalize(d.diagnosis);
      if (n && !byName.has(n)) byName.set(n, d);
    }
    return { byCode, byName };
  }, [catalog]);

  const resolveDiagnosis = (dx) => {
    if (!dx) return null;
    const code = String(dx.code || dx.codigo || dx.id || '').trim();
    let cat = code ? catalogByCode.byCode.get(code) : null;
    if (!cat) {
      const name = normalize(dx.name || dx.descripcion || dx.diagnosis || '');
      cat = catalogByCode.byName.get(name);
    }
    return cat ? { ...cat, via: cat.group } : null;
  };

  useEffect(() => {
    getCollection('diagnoses')
      .then((data) => setCatalog(data || []))
      .catch((err) => { console.error(err); setError('Error cargando catálogo de diagnósticos.'); });
    getCollection('patients')
      .then((data) => setPatients(data || []))
      .catch((err) => { console.error(err); setError((e) => e || 'Error cargando pacientes.'); });
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        let clinicalQuery, egresosQuery, surgeryQuery;
        if (year === 'all') {
          clinicalQuery = collection(db, 'clinicalRecords');
          egresosQuery = collection(db, 'egresosHospitalarios');
          surgeryQuery = collection(db, 'surgeryProtocols');
        } else {
          const m = Number(month);
          const hasMonth = !Number.isNaN(m) && m >= 1 && m <= 12;
          const startMonth = hasMonth ? m : 1;
          const endMonth = hasMonth ? m : 12;
          const startDate = new Date(year, startMonth - 1, 1, 0, 0, 0, 0);
          const endDate = new Date(year, endMonth, 0, 23, 59, 59, 999);
          const pad = (v) => String(v).padStart(2, '0');
          const startStr = `${year}-${pad(startMonth)}-01`;
          const endStr = `${year}-${pad(endMonth)}-${pad(new Date(year, endMonth, 0).getDate())}`;
          clinicalQuery = query(
            collection(db, 'clinicalRecords'),
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate))
          );
          egresosQuery = query(
            collection(db, 'egresosHospitalarios'),
            where('fechaIngreso', '>=', startStr),
            where('fechaIngreso', '<=', endStr)
          );
          surgeryQuery = query(
            collection(db, 'surgeryProtocols'),
            where('date', '>=', startStr),
            where('date', '<=', endStr)
          );
        }

        const [clinicalSnap, egresosSnap, protocolsSnap] = await Promise.all([
          getDocs(clinicalQuery),
          getDocs(egresosQuery),
          getDocs(surgeryQuery),
        ]);

        const records = clinicalSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const egresos = egresosSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const surgeries = protocolsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const unified = [];

        for (const rec of records) {
          const dxList = Array.isArray(rec.diagnosticos) ? rec.diagnosticos : [];
          const resolved = dxList.map(resolveDiagnosis).filter(Boolean);
          const groups = unique(resolved.map((c) => canonicalGroup(c.group) || c.group || 'Sin clasificar'));
          const allText = dxList.map((d) => `${d?.codigo || ''} ${d?.descripcion || ''} ${d?.tipo || ''} ${rec.motivoConsulta || ''}`).join(' ');
          const displayText = dxList.map((d) => d?.descripcion || '').filter(Boolean).join(' | ');
          const p = patientsMap.byId.get(rec.patientId);
          const age = calcAgeYears(p?.birthDate);
          unified.push({
            id: rec.id,
            source: 'clinicalRecords',
            sourceLabel: 'Ficha SOAP',
            date: rec.createdAt,
            patientId: rec.patientId,
            patientName: rec.patientName || p?.name || '–',
            species: p?.species || rec.patientSpecies || '–',
            breed: p?.breed || rec.patientBreed || '–',
            sex: p?.sex || rec.patientSex || '–',
            age,
            diagnosisText: allText,
            diagnosisDisplay: displayText,
            groups,
            diagnoses: dxList,
            professional: rec.vetNombre || '',
            outcome: '',
            complications: false,
            deceased: false,
            procedure: '',
            raw: rec,
          });
        }

        for (const eg of egresos) {
          const dxE = eg.diagnosticoEgreso;
          const dxI = eg.diagnosticoIngreso;
          const dxP = eg.diagnosticoPrincipal;
          const dxUsado = dxE || dxI || dxP || {};
          const resolvedE = resolveDiagnosis(dxUsado);
          const group = resolvedE ? canonicalGroup(resolvedE.group) || resolvedE.group : 'Sin clasificar';
          const p = patientsMap.byId.get(eg.patientId) || patientsMap.byFicha.get(String(eg.ficha || '').trim());
          const age = calcAgeYears(p?.birthDate);
          const dias = typeof eg.diasHospitalizacion === 'number' ? eg.diasHospitalizacion : 0;
          const complico = eg.complicaciones === true || eg.complicacionQx === true || eg.complicacionPostQx === true;
          const fallecio = eg.fallecido === true || eg.FALLECIDO === true;
          unified.push({
            id: eg.id,
            source: 'egresosHospitalarios',
            sourceLabel: 'Egreso hospitalario',
            date: eg.fechaIngreso ? new Date(eg.fechaIngreso) : null,
            patientId: p?.id || eg.patientId,
            patientName: eg.patientName || p?.name || '–',
            species: p?.species || eg.patientSpecies || '–',
            breed: p?.breed || eg.patientBreed || '–',
            sex: p?.sex || eg.patientSex || '–',
            age,
            diagnosisText: `${dxUsado.code || ''} ${dxUsado.name || dxUsado.diagnosis || ''} ${eg.procedimientoPrincipal?.name || ''}`.trim(),
            diagnosisDisplay: `${dxUsado.name || dxUsado.diagnosis || ''}${eg.procedimientoPrincipal?.name ? ' | ' + eg.procedimientoPrincipal.name : ''}`.trim(),
            groups: [group],
            diagnoses: [dxUsado],
            professional: eg.medicoAlta || '',
            outcome: eg.tipoAltaNormalizado || eg.condicionAlta || '',
            complications: complico,
            deceased: fallecio,
            procedure: eg.procedimientoPrincipal?.name || '',
            days: dias,
            raw: eg,
          });
        }

        for (const p of surgeries) {
          const sp = p.surgeonProtocol || {};
          const dxPre = sp.preOpDiagnosis;
          const dxPost = sp.postOpDiagnosis;
          const dxUsado = dxPre || dxPost;
          const resolvedP = resolveDiagnosis(dxUsado);
          const group = resolvedP ? canonicalGroup(resolvedP.group) || resolvedP.group : 'Sin clasificar';
          const pt = patientsMap.byId.get(p.patientId);
          const age = calcAgeYears(pt?.birthDate);
          const complText = String(sp.complications || '').trim();
          const complico = sp.hasComplications === true || (!!complText && !/^(no|ninguna?|sin complicaciones?|s\/c|n\/a|-|0)$/i.test(complText));
          unified.push({
            id: p.id,
            source: 'surgeryProtocols',
            sourceLabel: 'Protocolo quirúrgico',
            date: p.date ? new Date(p.date) : null,
            patientId: p.patientId,
            patientName: p.patientName || pt?.name || '–',
            species: pt?.species || p.patientSpecies || '–',
            breed: pt?.breed || p.patientBreed || '–',
            sex: pt?.sex || p.patientSex || '–',
            age,
            diagnosisText: `${sp.preOpDiagnosis || ''} ${sp.postOpDiagnosis || ''} ${p.procedureType || ''}`.trim(),
            diagnosisDisplay: [extractName(sp.preOpDiagnosis), extractName(sp.postOpDiagnosis)].filter(Boolean).join(' | ') || p.procedureType || '',
            groups: [group],
            diagnoses: [dxPre, dxPost].filter(Boolean),
            professional: p.mainSurgeon || '',
            outcome: complico ? complText : '',
            complications: complico,
            deceased: false,
            procedure: p.procedureType || '',
            raw: p,
          });
        }

        setCases(unified);
        setPage(0);
      } catch (err) {
        console.error(err);
        setError('Error cargando casos clínicos.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, month, patientsMap, catalogByCode]);

  const allSpecies = useMemo(() => unique(cases.map((c) => c.species).filter(Boolean)), [cases]);
  const allGroups = useMemo(() => {
    const set = new Set();
    for (const c of cases) c.groups.forEach((g) => set.add(g));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [cases]);
  const allVets = useMemo(() => unique(cases.map((c) => c.professional).filter(Boolean)), [cases]);
  const allBreeds = useMemo(() => unique(cases.map((c) => c.breed).filter(Boolean)), [cases]);

  const filtered = useMemo(() => {
    const q = normalize(debouncedQ);
    const sourceSet = new Set(filters.source);
    const speciesSet = new Set(filters.species);
    const groupSet = new Set(filters.group);
    const minAge = toNumber(filters.ageMin);
    const maxAge = toNumber(filters.ageMax);
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
    if (toDate) toDate.setHours(23, 59, 59, 999);
    const dn = normalize(filters.diagnosis);
    const vetN = normalize(filters.vet);
    const breedN = normalize(filters.breed);

    return cases.filter((c) => {
      if (sourceSet.size > 0 && !sourceSet.has(c.source)) return false;
      if (speciesSet.size > 0 && !speciesSet.has(c.species)) return false;
      if (breedN && !normalize(c.breed).includes(breedN)) return false;
      if (filters.sex && c.sex !== filters.sex) return false;
      if (minAge != null && (c.age == null || c.age < minAge)) return false;
      if (maxAge != null && (c.age == null || c.age > maxAge)) return false;
      if (fromDate) {
        const d = getDate(c.date);
        if (!d || d < fromDate) return false;
      }
      if (toDate) {
        const d = getDate(c.date);
        if (!d || d > toDate) return false;
      }
      if (dn && !normalize(c.diagnosisText).includes(dn)) return false;
      if (groupSet.size > 0 && !c.groups.some((g) => groupSet.has(g))) return false;
      if (vetN && !normalize(c.professional).includes(vetN)) return false;
      if (filters.outcome && !normalize(c.outcome).includes(normalize(filters.outcome))) return false;
      if (filters.complications !== 'all' && c.complications !== (filters.complications === 'yes')) return false;
      if (filters.deceased !== 'all' && c.deceased !== (filters.deceased === 'yes')) return false;
      if (q) {
        const text = `${c.patientName} ${c.species} ${c.breed} ${c.diagnosisText} ${c.professional} ${c.outcome}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      const da = getDate(b.date) || new Date(0);
      const db = getDate(a.date) || new Date(0);
      return da - db;
    });
  }, [cases, filters, debouncedQ]);

  const handleFilterChange = (field, value) => setFilters((p) => ({ ...p, [field]: value }));

  const handleDownload = () => {
    const headers = ['ID', 'Fuente', 'Fecha', 'Paciente', 'Especie', 'Raza', 'Edad', 'Sexo', 'Diagnósticos', 'Grupos', 'Profesional', 'Complicaciones', 'Fallecido', 'Evolución', 'Procedimiento'];
    const rows = filtered.map((c) => [
      c.id, c.sourceLabel, fmtDate(c.date), c.patientName, c.species, c.breed,
      c.age ?? '', c.sex, c.diagnosisText, c.groups.join(' | '), c.professional,
      c.complications ? 'Sí' : 'No', c.deceased ? 'Sí' : 'No', c.outcome, c.procedure,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((x) => `"${String(x || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `casos-clinicos-${year}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#F4F6F8' }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Búsqueda avanzada de casos clínicos
      </Typography>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por paciente, especie, diagnóstico, profesional, palabra clave..."
              value={filters.q}
              onChange={(e) => handleFilterChange('q', e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: MUTED, mr: 1 }}/> }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField select fullWidth size="small" label="Año" value={year} onChange={(e) => { setYear(e.target.value === 'all' ? 'all' : Number(e.target.value)); setPage(0); }}>
              <MenuItem value="all">Todos</MenuItem>
              {Array.from({ length: 10 }, (_, i) => today.getFullYear() - i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField select fullWidth size="small" label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} disabled={year === 'all'}>
              <MenuItem value="all">Todo el año</MenuItem>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
              Filtros avanzados
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              multiple
              size="small"
              options={allSpecies}
              value={filters.species}
              onChange={(_, v) => handleFilterChange('species', v)}
              renderInput={(p) => <TextField {...p} label="Especie"/>}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              freeSolo
              size="small"
              options={allBreeds}
              value={filters.breed}
              onChange={(_, v) => handleFilterChange('breed', v || '')}
              inputValue={filters.breed}
              onInputChange={(_, v) => handleFilterChange('breed', v)}
              renderInput={(p) => <TextField {...p} label="Raza"/>}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sexo</InputLabel>
              <Select value={filters.sex} onChange={(e) => handleFilterChange('sex', e.target.value)} label="Sexo">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Macho">Macho</MenuItem>
                <MenuItem value="Hembra">Hembra</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField fullWidth size="small" label="Edad min" type="number" value={filters.ageMin} onChange={(e) => handleFilterChange('ageMin', e.target.value)}/>
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField fullWidth size="small" label="Edad max" type="number" value={filters.ageMax} onChange={(e) => handleFilterChange('ageMax', e.target.value)}/>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField fullWidth size="small" label="Desde" type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)} InputLabelProps={{ shrink: true }}/>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField fullWidth size="small" label="Hasta" type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)} InputLabelProps={{ shrink: true }}/>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              multiple
              size="small"
              options={FUENTES}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.key === v.key}
              value={filters.source.map((k) => FUENTES.find((o) => o.key === k) || { key: k, label: k })}
              onChange={(_, v) => handleFilterChange('source', v.map((o) => o.key))}
              renderInput={(p) => <TextField {...p} label="Fuente del caso"/>}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              multiple
              size="small"
              options={allGroups}
              value={filters.group}
              onChange={(_, v) => handleFilterChange('group', v)}
              renderInput={(p) => <TextField {...p} label="Grupo de enfermedad"/>}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              freeSolo
              size="small"
              options={allVets}
              value={filters.vet}
              onChange={(_, v) => handleFilterChange('vet', v || '')}
              inputValue={filters.vet}
              onInputChange={(_, v) => handleFilterChange('vet', v)}
              renderInput={(p) => <TextField {...p} label="Profesional"/>}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField fullWidth size="small" label="Diagnóstico contiene" value={filters.diagnosis} onChange={(e) => handleFilterChange('diagnosis', e.target.value)}/>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Complicaciones</InputLabel>
              <Select value={filters.complications} onChange={(e) => handleFilterChange('complications', e.target.value)} label="Complicaciones">
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="yes">Sí</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Fallecido</InputLabel>
              <Select value={filters.deceased} onChange={(e) => handleFilterChange('deceased', e.target.value)} label="Fallecido">
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="yes">Sí</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <Typography variant="body2" color="text.secondary">
              {filtered.length} caso{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon/>} onClick={handleDownload}>
              Exportar CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress/></Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    {['Fecha', 'Fuente', 'Paciente', 'Especie', 'Raza', 'Edad', 'Sexo', 'Diagnóstico / procedimiento', 'Grupo', 'Profesional', 'Evolución'].map((h) => (
                      <TableCell key={h} sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: '#F8FAFC' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => (
                    <TableRow key={`${c.source}-${c.id}`} hover sx={{ cursor: 'pointer' }} onClick={() => c.patientId && navigate(`/patients/${c.patientId}/record`)}>
                      <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{fmtDate(c.date)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={c.sourceLabel} sx={{ fontSize: '0.62rem', bgcolor: FUENTE_COLOR[c.source], fontWeight: 600 }}/>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{c.patientName}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{c.species}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{c.breed}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{c.age != null ? `${c.age} a` : '–'}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{c.sex}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', maxWidth: 240 }}>
                        <Typography noWrap title={c.diagnosisDisplay}>{c.diagnosisDisplay}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>
                        {c.groups.slice(0, 2).map((g, i) => (
                          <Chip key={i} size="small" label={g} sx={{ fontSize: '0.6rem', mr: 0.5, mb: 0.2, bgcolor: `${PRIMARY}15`, color: PRIMARY }}/>
                        ))}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{c.professional || '–'}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>
                        {c.deceased ? <Chip size="small" label="Fallecido" color="error" sx={{ fontSize: '0.6rem' }}/> : null}
                        {c.complications ? <Chip size="small" label="Complicado" color="warning" sx={{ fontSize: '0.6rem' }}/> : null}
                        {c.outcome ? <Typography variant="caption" noWrap>{c.outcome}</Typography> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4, color: MUTED }}>
                        No se encontraron casos con los filtros aplicados.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas"
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
